import React from 'react';
import PropTypes from 'prop-types';
import { css, StyleSheet } from 'aphrodite';
import compose from 'recompose/compose';
import defaultProps from 'recompose/defaultProps';
import withHandlers from 'recompose/withHandlers';
import withProps from 'recompose/withProps';
import withState from 'recompose/withState';
import { childrenPropType, stylePropType, TSS_GREEN, TSS_DARK_BLUE, TSS_BLUE_1, range } from 'tss-core-lib-common';
import Icon from 'react-svg-ikon';

import { IMG_ICON_CHEVRON_LEFT, IMG_ICON_CHEVRON_RIGHT, IMG_ICON_CHEVRON_STOP_RIGHT, IMG_ICON_CHEVRON_STOP_LEFT } from '../img';

var styles = void 0;

var enhancePage = withHandlers({
  onClick: function onClick(props) {
    return function () {
      return props.onClick && props.onClick(props.pageNumber);
    };
  }
});

var Page = function Page(_ref) {
  var children = _ref.children,
      disabled = _ref.disabled,
      style = _ref.style,
      currentPage = _ref.currentPage,
      onClick = _ref.onClick;
  return React.createElement(
    'div',
    {
      className: css(styles.page, currentPage && styles.currentPage, style, disabled && styles.disabledPage),
      tabIndex: 0,
      role: 'button',
      onClick: onClick
    },
    children,
    currentPage ? React.createElement('div', { className: css(styles.currentPageBorder) }) : null
  );
};

Page.propTypes = {
  children: childrenPropType.isRequired,
  pageNumber: PropTypes.number, // eslint-disable-line
  disabled: PropTypes.bool,
  style: stylePropType,
  currentPage: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};

Page.defaultProps = {
  style: null,
  pageNumber: null,
  currentPage: false,
  disabled: false
};

Page = enhancePage(Page);

var enhancePaginator = compose(defaultProps({
  margin: 3,
  currentPage: 1,
  totalPages: 1
}),
// Protect against sloppy usage and force currentPage to no be higher than totalPages
withProps(function (props) {
  return {
    currentPage: Math.min(props.currentPage, props.totalPages)
  };
}), withState('leftMargin', 'setLeftMargin', function (props) {
  return Math.max(1, props.currentPage - props.margin);
}), withState('rightMargin', 'setRightMargin', function (props) {
  return Math.min(props.totalPages, props.currentPage + props.margin);
}), withHandlers({
  onPageClick: function onPageClick(props) {
    return function (pageNumber) {
      return props.onChange && props.onChange(pageNumber);
    };
  }
}));

var Paginator = function Paginator(_ref2) {
  var totalPages = _ref2.totalPages,
      currentPage = _ref2.currentPage,
      margin = _ref2.margin,
      onPageClick = _ref2.onPageClick;

  var leftMargin = Math.max(2, currentPage - margin);
  var rightMargin = Math.min(totalPages - 1, currentPage + margin);
  var leftDiff = currentPage - leftMargin;
  var rightDiff = rightMargin - currentPage;
  if (leftDiff > rightDiff) {
    leftMargin = Math.max(2, leftMargin - (margin - rightDiff));
  } else {
    rightMargin = Math.min(totalPages - 1, rightMargin + (margin - leftDiff));
  }

  var prevPageDisabled = currentPage <= 1;
  var nextPageDisabled = currentPage >= totalPages;

  var prevControls = totalPages >= margin * 2 ? [React.createElement(
    Page,
    {
      key: 'first',
      disabled: prevPageDisabled,
      pageNumber: 1,
      style: styles.borderRight,
      onClick: onPageClick
    },
    React.createElement(Icon, { src: IMG_ICON_CHEVRON_STOP_LEFT, stroke: TSS_DARK_BLUE(prevPageDisabled ? 0.33 : 1) })
  ), React.createElement(
    Page,
    {
      key: 'prev',
      disabled: prevPageDisabled,
      pageNumber: Math.max(1, currentPage - 1),
      style: styles.borderRight,
      onClick: onPageClick
    },
    React.createElement(Icon, { src: IMG_ICON_CHEVRON_LEFT, stroke: TSS_DARK_BLUE(prevPageDisabled ? 0.33 : 1) })
  )] : null;

  var nextControls = totalPages >= margin * 2 ? [React.createElement(
    Page,
    {
      key: 'next',
      disabled: nextPageDisabled,
      pageNumber: Math.min(totalPages, currentPage + 1),
      style: styles.borderLeft,
      onClick: onPageClick
    },
    React.createElement(Icon, { src: IMG_ICON_CHEVRON_RIGHT, stroke: TSS_DARK_BLUE(nextPageDisabled ? 0.33 : 1) })
  ), React.createElement(
    Page,
    {
      key: 'last',
      disabled: nextPageDisabled,
      pageNumber: totalPages,
      style: styles.borderLeft,
      onClick: onPageClick
    },
    React.createElement(Icon, { src: IMG_ICON_CHEVRON_STOP_RIGHT, stroke: TSS_DARK_BLUE(nextPageDisabled ? 0.33 : 1) })
  )] : null;

  var showNextPageGroup = rightMargin < totalPages && currentPage < totalPages - margin - 1;

  return React.createElement(
    'div',
    { className: css(styles.container) },
    prevControls,
    leftMargin > 1 && currentPage > margin + 2 ? React.createElement(
      Page,
      { pageNumber: Math.max(1, currentPage - margin * 2), onClick: onPageClick },
      '...'
    ) : React.createElement(
      Page,
      { currentPage: currentPage === 1, pageNumber: 1, onClick: onPageClick },
      '1'
    ),
    range(currentPage - leftMargin).map(function (i) {
      return React.createElement(
        Page,
        { key: i, pageNumber: leftMargin + i, onClick: onPageClick },
        leftMargin + i
      );
    }),
    currentPage >= leftMargin && currentPage <= rightMargin ? React.createElement(
      Page,
      { currentPage: true },
      currentPage
    ) : null,
    range(rightMargin - currentPage).map(function (i) {
      return React.createElement(
        Page,
        { key: i, pageNumber: currentPage + i + 1, onClick: onPageClick },
        currentPage + i + 1
      );
    }),
    showNextPageGroup ? React.createElement(
      Page,
      { pageNumber: Math.min(totalPages, currentPage + margin * 2), onClick: onPageClick },
      '...'
    ) : null,
    !showNextPageGroup && totalPages > 1 ? React.createElement(
      Page,
      { currentPage: currentPage === totalPages, pageNumber: totalPages, onClick: onPageClick },
      totalPages
    ) : null,
    nextControls
  );
};

Paginator.propTypes = {
  totalPages: PropTypes.number.isRequired,
  margin: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageClick: PropTypes.func.isRequired
};

Paginator.defaultProps = {};

styles = StyleSheet.create({
  container: {
    border: '2px solid ' + TSS_BLUE_1(),
    display: 'flex'
  },

  page: {
    margin: '0px 1px',
    padding: '0px 10px',
    position: 'relative',
    minWidth: '4.6rem',
    height: '4.6rem',
    fontSize: '1.6rem',
    color: TSS_DARK_BLUE(),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 0
  },

  borderRight: {
    borderRight: '1px solid ' + TSS_BLUE_1()
  },

  borderLeft: {
    borderLeft: '1px solid ' + TSS_BLUE_1()
  },

  disabledPage: {
    cursor: 'not-allowed',
    color: TSS_DARK_BLUE(0.33)
  },

  currentPage: {
    color: TSS_GREEN(),
    fontWeight: 600
  },

  currentPageBorder: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: TSS_GREEN()
  }
});

export default enhancePaginator(Paginator);